
import React, { useState, useEffect } from 'react';
import { Bot, Plus, Trash2, MessageSquare, Sparkles, Cpu, Copy, Save, Download, Upload, Wand2, Send } from 'lucide-react';
import { PREBUILT_AGENTS, executeRuleAgent, type Agent, type Rule } from './lib/engine';
import { aiChat } from './lib/ai-fallback';
import { SettingsButton } from './lib/SettingsButton';

interface ChatMessage { role: 'user' | 'agent'; content: string; mode?: string; highlights?: any[]; }
interface SavedAgent extends Agent { chats: { id: string; title: string; messages: ChatMessage[]; date: string }[]; }

export default function App() {
  const [agents, setAgents] = useState<SavedAgent[]>(() => {
    const saved = JSON.parse(localStorage.getItem('ca_agents') || '[]');
    return saved.length > 0 ? saved : PREBUILT_AGENTS.map(a => ({ ...a, chats: [] }));
  });
  const [activeAgent, setActiveAgent] = useState<SavedAgent | null>(null);
  const [activeChat, setActiveChat] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [aiMode, setAiMode] = useState(false);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [showCreator, setShowCreator] = useState(false);
  const [newAgent, setNewAgent] = useState({ name: '', systemPrompt: '', outputFormat: 'text' });
  const [newRules, setNewRules] = useState<Rule[]>([]);

  useEffect(() => { localStorage.setItem('ca_agents', JSON.stringify(agents)); }, [agents]);

  const selectAgent = (agent: SavedAgent) => {
    setActiveAgent(agent);
    setMessages([]);
    setActiveChat(null);
    setAiMode(false);
  };

  const sendMessage = async () => {
    if (!activeAgent || !input.trim()) return;
    const userMsg: ChatMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');

    if (aiMode) {
      setIsAiLoading(true);
      let aiText = '';
      const agentMsg: ChatMessage = { role: 'agent', content: '', mode: 'AI (Groq)' };
      setMessages(prev => [...prev, agentMsg]);
      await aiChat(activeAgent.systemPrompt, userMsg.content, (chunk) => {
        aiText += chunk;
        setMessages(prev => prev.map((m, i) => i === prev.length - 1 ? { ...m, content: aiText } : m));
      });
      setIsAiLoading(false);
    } else {
      const result = executeRuleAgent(activeAgent, userMsg.content);
      const agentMsg: ChatMessage = { role: 'agent', content: result.output, mode: 'Rule Engine', highlights: result.highlights };
      setMessages(prev => [...prev, agentMsg]);
    }
  };

  const saveChat = () => {
    if (!activeAgent || messages.length === 0) return;
    const chat = { id: Date.now().toString(), title: messages[0].content.slice(0, 40) + '...', messages: [...messages], date: new Date().toLocaleDateString() };
    const updated = agents.map(a => a.id === activeAgent.id ? { ...a, chats: [chat, ...a.chats] } : a);
    setAgents(updated);
    localStorage.setItem('ca_agents', JSON.stringify(updated));
    setActiveChat(chat.id);
  };

  const createAgent = () => {
    if (!newAgent.name.trim()) return;
    const agent: SavedAgent = { id: 'custom_' + Date.now(), name: newAgent.name, systemPrompt: newAgent.systemPrompt, rules: newRules, outputFormat: newAgent.outputFormat, created: new Date().toLocaleDateString(), chats: [] };
    setAgents(prev => [...prev, agent]);
    localStorage.setItem('ca_agents', JSON.stringify([...agents, agent]));
    setShowCreator(false);
    setNewAgent({ name: '', systemPrompt: '', outputFormat: 'text' });
    setNewRules([]);
  };

  const exportAgents = () => {
    const blob = new Blob([JSON.stringify(agents, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'catalystagile-agents.json'; a.click();
  };

  const importAgents = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { try { const data = JSON.parse(reader.result as string); if (Array.isArray(data)) setAgents(data); } catch {} };
    reader.readAsText(file);
  };

  return (
    <div className="min-h-screen bg-[#050b14] text-[#f0f0f0] flex">
      {/* Sidebar */}
      <aside className="w-72 bg-[#0a1628] border-r border-white/10 flex flex-col">
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-[#d4af37] flex items-center justify-center text-[#0a1628] font-bold">C</div>
            <div>
              <h1 className="text-sm font-semibold text-[#f0f0f0]">AI Prompt Agent Builder</h1>
              <p className="text-[10px] text-[#8a9bb8]">Rule Engine + AI Mode</p>
            </div>
          </div>
          <button onClick={() => setShowCreator(true)} className="w-full px-3 py-2 rounded-lg bg-[#d4af37] text-[#0a1628] text-xs font-semibold hover:bg-[#e8c547] transition-colors flex items-center justify-center gap-1.5">
            <Plus className="w-3.5 h-3.5"/> New Agent
          </button>
          <div className="mt-2">
            <SettingsButton className="w-full px-3 py-2 rounded-lg text-xs bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 inline-flex items-center justify-center gap-1.5 transition-colors text-[#8a9bb8] hover:text-[#d4af37]" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {agents.map(agent => (
            <button key={agent.id} onClick={() => selectAgent(agent)}
              className={`w-full text-left px-3 py-2.5 rounded-lg text-xs transition-colors flex items-center gap-2 ${activeAgent?.id === agent.id ? 'bg-[#d4af37]/20 border border-[#d4af37]/40' : 'hover:bg-[#132238]'}`}>
              <Bot className="w-4 h-4 text-[#8a9bb8]"/>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#f0f0f0] truncate">{agent.name}</div>
                <div className="text-[10px] text-[#8a9bb8] flex items-center gap-1">
                  {agent.created === 'built-in' ? <Cpu className="w-3 h-3"/> : <Wand2 className="w-3 h-3"/>}
                  {agent.created === 'built-in' ? 'Built-in' : 'Custom'} · {agent.chats?.length || 0} chats
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="p-3 border-t border-white/10 space-y-2">
          <button onClick={exportAgents} className="w-full px-3 py-2 rounded-lg bg-[#132238] text-[#8a9bb8] text-xs hover:bg-[#1a2d4a] transition-colors flex items-center gap-1.5"><Download className="w-3.5 h-3.5"/> Export Agents</button>
          <label className="w-full px-3 py-2 rounded-lg bg-[#132238] text-[#8a9bb8] text-xs hover:bg-[#1a2d4a] transition-colors flex items-center gap-1.5 cursor-pointer"><Upload className="w-3.5 h-3.5"/> Import Agents<input type="file" accept=".json" className="hidden" onChange={importAgents}/></label>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col">
        {activeAgent ? (
          <>
            <div className="px-4 py-3 border-b border-white/10 bg-[#0a1628] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bot className="w-5 h-5 text-[#d4af37]"/>
                <div>
                  <h2 className="text-sm font-semibold text-[#f0f0f0]">{activeAgent.name}</h2>
                  <p className="text-[10px] text-[#8a9bb8]">{activeAgent.systemPrompt.slice(0, 60)}...</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setAiMode(!aiMode)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${aiMode ? 'bg-[#d4af37] text-[#0a1628]' : 'bg-[#132238] text-[#8a9bb8] hover:bg-[#1a2d4a]'}`}>
                  <Sparkles className="w-3.5 h-3.5"/> {aiMode ? 'AI Mode ON' : 'AI Mode OFF'}
                </button>
                <button onClick={saveChat} className="px-3 py-1.5 rounded-lg bg-[#132238] text-[#8a9bb8] hover:bg-[#1a2d4a] text-xs transition-colors flex items-center gap-1.5"><Save className="w-3.5 h-3.5"/> Save Chat</button>
              </div>
            </div>

            {/* Chat History */}
            {activeAgent.chats && activeAgent.chats.length > 0 && (
              <div className="px-4 py-2 border-b border-white/10 bg-[#070f1a]/50 flex gap-2 overflow-x-auto">
                {activeAgent.chats.map(chat => (
                  <button key={chat.id} onClick={() => { setActiveChat(chat.id); setMessages(chat.messages); }}
                    className={`px-3 py-1.5 rounded-lg text-[10px] whitespace-nowrap transition-colors ${activeChat === chat.id ? 'bg-[#d4af37]/20 text-[#d4af37] border border-[#d4af37]/40' : 'bg-[#132238] text-[#8a9bb8] hover:bg-[#1a2d4a]'}`}>
                    {chat.title} · {chat.date}
                  </button>
                ))}
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 && (
                <div className="text-center py-12">
                  <Bot className="w-12 h-12 text-[#8a9bb8] mx-auto mb-4"/>
                  <p className="text-sm text-[#8a9bb8]">Start a conversation with {activeAgent.name}</p>
                  <p className="text-xs text-[#8a9bb8] mt-1">{aiMode ? 'AI Mode: Responses powered by Groq' : 'Rule Mode: Responses processed by local engine'}</p>
                </div>
              )}
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-3xl rounded-xl px-4 py-3 ${msg.role === 'user' ? 'bg-[#d4af37] text-[#0a1628]' : 'bg-[#132238] border border-white/10 text-[#f0f0f0]'}`}>
                    {msg.role === 'agent' && msg.mode && (
                      <div className="flex items-center gap-1.5 mb-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${msg.mode.includes('AI') ? 'bg-[#d4af37]/20 text-[#d4af37]' : 'bg-green-500/20 text-green-400'}`}>{msg.mode}</span>
                      </div>
                    )}
                    <div className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</div>
                    {msg.role === 'agent' && (
                      <button onClick={() => navigator.clipboard.writeText(msg.content)} className="mt-2 text-[10px] text-[#8a9bb8] hover:text-[#d4af37] flex items-center gap-1 transition-colors"><Copy className="w-3 h-3"/> Copy</button>
                    )}
                  </div>
                </div>
              ))}
              {isAiLoading && (
                <div className="flex justify-start">
                  <div className="bg-[#132238] border border-white/10 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-2 text-xs text-[#8a9bb8]"><Sparkles className="w-3.5 h-3.5 animate-pulse"/> AI is thinking...</div>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="p-4 border-t border-white/10 bg-[#0a1628]">
              <div className="flex gap-2">
                <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder={`Message ${activeAgent.name}...`}
                  className="flex-1 bg-[#070f1a] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#f0f0f0] placeholder-[#8a9bb8]/50 focus:outline-none focus:border-[#d4af37]/50 resize-none h-11 leading-5"/>
                <button onClick={sendMessage} disabled={!input.trim() || isAiLoading} className="px-4 rounded-lg bg-[#d4af37] text-[#0a1628] font-semibold hover:bg-[#e8c547] transition-colors disabled:opacity-50">
                  <Send className="w-4 h-4"/>
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Bot className="w-16 h-16 text-[#8a9bb8] mx-auto mb-4"/>
              <h2 className="text-lg font-semibold text-[#f0f0f0] mb-2">Select an Agent</h2>
              <p className="text-sm text-[#8a9bb8]">Choose a built-in agent or create your own custom agent.</p>
            </div>
          </div>
        )}
      </main>

      {/* Agent Creator Modal */}
      {showCreator && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0f1d32] rounded-xl border border-white/10 w-full max-w-lg max-h-[80vh] overflow-y-auto p-6">
            <h2 className="text-lg font-semibold text-[#d4af37] mb-4">Create Custom Agent</h2>
            <div className="space-y-3">
              <div><label className="text-xs text-[#8a9bb8] mb-1 block">Agent Name</label><input value={newAgent.name} onChange={e => setNewAgent({ ...newAgent, name: e.target.value })} className="w-full bg-[#070f1a] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#d4af37]/50"/></div>
              <div><label className="text-xs text-[#8a9bb8] mb-1 block">System Prompt (instructions for the agent)</label><textarea value={newAgent.systemPrompt} onChange={e => setNewAgent({ ...newAgent, systemPrompt: e.target.value })} rows={3} className="w-full bg-[#070f1a] border border-white/10 rounded-lg p-3 text-sm focus:outline-none focus:border-[#d4af37]/50 resize-none"/></div>
              <div><label className="text-xs text-[#8a9bb8] mb-1 block">Output Format</label>
                <select value={newAgent.outputFormat} onChange={e => setNewAgent({ ...newAgent, outputFormat: e.target.value })} className="w-full bg-[#070f1a] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#d4af37]/50">
                  <option value="text">Plain Text</option><option value="structured">Structured (with highlights)</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-[#8a9bb8] mb-1 block">Rules (regex find/replace)</label>
                <div className="space-y-2">
                  {newRules.map((rule, i) => (
                    <div key={i} className="flex gap-2">
                      <input value={rule.find || ''} onChange={e => { const r = [...newRules]; r[i] = { ...r[i], find: e.target.value }; setNewRules(r); }} placeholder="Find regex" className="flex-1 bg-[#070f1a] border border-white/10 rounded-lg px-2 py-1.5 text-xs"/>
                      <input value={rule.replace || ''} onChange={e => { const r = [...newRules]; r[i] = { ...r[i], replace: e.target.value }; setNewRules(r); }} placeholder="Replace" className="flex-1 bg-[#070f1a] border border-white/10 rounded-lg px-2 py-1.5 text-xs"/>
                      <button onClick={() => setNewRules(prev => prev.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4"/></button>
                    </div>
                  ))}
                  <button onClick={() => setNewRules(prev => [...prev, { find: '', replace: '' }])} className="text-xs text-[#d4af37] hover:text-[#e8c547]">+ Add Rule</button>
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-6">
              <button onClick={() => setShowCreator(false)} className="px-4 py-2 rounded-lg bg-[#132238] text-[#8a9bb8] text-sm hover:bg-[#1a2d4a] transition-colors">Cancel</button>
              <button onClick={createAgent} className="px-4 py-2 rounded-lg bg-[#d4af37] text-[#0a1628] font-semibold text-sm hover:bg-[#e8c547] transition-colors">Create Agent</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
